import React, { useState, useEffect } from 'react';
import { ViewLayout } from '../ui';
import { ChevronRight, ChevronDown, Plus, Edit, Trash2, Move, FolderTree } from 'lucide-react';
import useSupabase from '../../hooks/useSupabase';
import { useToast } from '../ui/ToastContext';

const CategoryManagementView = ({ currentUser }) => {
  const { toast } = useToast();
  const { 
    fetchCategoryTree, 
    createCategory, 
    updateCategory, 
    deleteCategory,
    getCategoryStats,
    getHierarchicalCategoryStats 
  } = useSupabase();

  const [categories, setCategories] = useState([]);
  const [categoryStats, setCategoryStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [editingCategory, setEditingCategory] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCategoryData, setNewCategoryData] = useState({
    name: '',
    description: '',
    parent_id: null,
    sort_order: 0
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const [treeData, statsData] = await Promise.all([
        fetchCategoryTree(),
        getHierarchicalCategoryStats()
      ]);
      
      setCategories(treeData);
      setCategoryStats(statsData);
      
      // Debug logging to verify hierarchical counts
      console.log('📊 Hierarchical category stats loaded:', statsData.map(stat => ({
        name: stat.name,
        propCount: stat.propCount
      })));
      
      // Auto-expand root categories
      const rootIds = treeData.map(cat => cat.id);
      setExpandedNodes(new Set(rootIds));
    } catch (err) {
      toast.error('Failed to load categories', { title: 'Load Error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    try {
      await createCategory(newCategoryData);
      toast.success('Category added successfully!');
      setShowAddModal(false);
      setNewCategoryData({ name: '', description: '', parent_id: null, sort_order: 0 });
      loadCategories();
    } catch (err) {
      toast.error('Failed to add category', { title: 'Add Error' });
    }
  };

  const handleUpdateCategory = async (categoryId, data) => {
    try {
      await updateCategory(categoryId, data);
      toast.success('Category updated successfully!');
      setEditingCategory(null);
      loadCategories();
    } catch (err) {
      toast.error('Failed to update category', { title: 'Update Error' });
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure? Props in this category will be moved to parent category.')) {
      return;
    }

    try {
      await deleteCategory(categoryId);
      toast.success('Category deleted successfully!');
      loadCategories();
    } catch (err) {
      toast.error('Failed to delete category', { title: 'Delete Error' });
    }
  };

  const toggleExpanded = (categoryId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedNodes(newExpanded);
  };

  const getPropCount = (category, level = 0) => {
    const stats = categoryStats.find(s => s.id === category.id);
    const directCount = stats?.propCount || 0;
    
    // For root categories (level 0), show hierarchical count including subcategories
    if (level === 0 && category.children && category.children.length > 0) {
      const subcategoryCount = category.children.reduce((sum, child) => {
        return sum + getPropCount(child, level + 1);
      }, 0);
      return directCount + subcategoryCount;
    }
    
    // For subcategories, show only direct count
    return directCount;
  };

  const renderCategoryNode = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedNodes.has(category.id);
    const propCount = getPropCount(category, level);
    
    return (
      <div key={category.id} className="select-none">
        <div 
          className={`flex items-center p-2 rounded-lg hover:bg-gray-50 group transition-colors ${
            level === 0 ? 'font-medium bg-gray-25' : ''
          }`}
          style={{ paddingLeft: `${level * 24 + 8}px` }}
        >
          {/* Expand/Collapse Button */}
          <button
            onClick={() => toggleExpanded(category.id)}
            className="p-1 rounded hover:bg-gray-200 mr-1"
            disabled={!hasChildren}
          >
            {hasChildren ? (
              isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
            ) : (
              <div className="w-4 h-4" />
            )}
          </button>

          {/* Category Info */}
          <FolderTree className={`h-4 w-4 mr-2 ${level === 0 ? 'text-blue-600' : level === 1 ? 'text-green-600' : 'text-orange-600'}`} />
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="font-medium text-gray-900 truncate">{category.name}</span>
                {propCount > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {propCount} props
                  </span>
                )}
              </div>
              
              {/* Actions */}
              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center space-x-1">
                <button
                  onClick={() => {
                    setNewCategoryData({ 
                      name: '', 
                      description: '', 
                      parent_id: category.id, 
                      sort_order: (category.children?.length || 0) + 1 
                    });
                    setShowAddModal(true);
                  }}
                  className="p-1 rounded hover:bg-gray-200 text-green-600"
                  title="Add subcategory"
                >
                  <Plus className="h-3 w-3" />
                </button>
                <button
                  onClick={() => setEditingCategory(category)}
                  className="p-1 rounded hover:bg-gray-200 text-blue-600"
                  title="Edit category"
                >
                  <Edit className="h-3 w-3" />
                </button>
                <button
                  onClick={() => handleDeleteCategory(category.id)}
                  className="p-1 rounded hover:bg-gray-200 text-red-600"
                  title="Delete category"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
            
            {category.description && (
              <p className="text-xs text-gray-500 truncate mt-1">{category.description}</p>
            )}
          </div>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div>
            {category.children.map(child => renderCategoryNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const stats = [
    { label: 'Total Categories', value: categoryStats.length, color: 'blue' },
    { label: 'Root Categories', value: categories.length, color: 'green' },
    { label: 'Categories with Props', value: categoryStats.filter(s => s.propCount > 0).length, color: 'purple' },
    { label: 'Total Props', value: categoryStats.reduce((sum, s) => sum + (s.propCount || 0), 0), color: 'orange' }
  ].map((stat, index) => (
    <ViewLayout.Stat key={index} title={stat.label} value={stat.value} color={stat.color} />
  ));

  if (loading) {
    return (
      <ViewLayout title="Category Management" subtitle="Loading categories...">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </ViewLayout>
    );
  }

  return (
    <ViewLayout
      title="Category Management"
      subtitle="Manage hierarchical prop categories"
      stats={stats}
      actions={[
        <button
          key="add-root"
          onClick={() => {
            setNewCategoryData({ name: '', description: '', parent_id: null, sort_order: categories.length + 1 });
            setShowAddModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Add Root Category</span>
        </button>
      ]}
    >
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b">
          <h3 className="font-medium text-gray-900">Category Hierarchy</h3>
          <p className="text-sm text-gray-600 mt-1">
            Click to expand/collapse • Hover for actions • {categoryStats.length} total categories
          </p>
          <p className="text-xs text-blue-600 mt-1">
            📊 Prop counts include subcategories (matches search filtering behavior)
          </p>
        </div>
        
        <div className="p-4 space-y-1 max-h-96 overflow-y-auto">
          {categories.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FolderTree className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No categories found</p>
            </div>
          ) : (
            <>
              {categories.map(category => renderCategoryNode(category))}
              {/* Add uncategorized category if it exists */}
              {categoryStats.find(s => s.id === 'uncategorized') && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="text-sm font-medium text-gray-700 mb-2">Uncategorized Props</div>
                  {renderCategoryNode({
                    id: 'uncategorized',
                    name: 'Uncategorized',
                    children: [],
                    level: 0,
                    parent_id: null
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || editingCategory) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editingCategory ? editingCategory.name : newCategoryData.name}
                  onChange={(e) => {
                    if (editingCategory) {
                      setEditingCategory({...editingCategory, name: e.target.value});
                    } else {
                      setNewCategoryData({...newCategoryData, name: e.target.value});
                    }
                  }}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Category name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editingCategory ? editingCategory.description : newCategoryData.description}
                  onChange={(e) => {
                    if (editingCategory) {
                      setEditingCategory({...editingCategory, description: e.target.value});
                    } else {
                      setNewCategoryData({...newCategoryData, description: e.target.value});
                    }
                  }}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Optional description"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingCategory(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (editingCategory) {
                    handleUpdateCategory(editingCategory.id, {
                      name: editingCategory.name,
                      description: editingCategory.description
                    });
                  } else {
                    handleAddCategory();
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {editingCategory ? 'Update' : 'Add'} Category
              </button>
            </div>
          </div>
        </div>
      )}
    </ViewLayout>
  );
};

export default CategoryManagementView;
